/**
 * Main Validation Engine
 * Orchestrates field validation
 */

import { FieldValidators } from './field_validators.js';
import { ErrorManager } from './error_manager.js';
import { FormConfig } from '../core/form_config.js';

export class Validator {
  constructor(formState, translator = null, formConfig = null) {
    this.formState = formState;
    this.translator = translator;
    this.formConfig = formConfig;
  }

  /**
   * Validate single field
   * @param {Element} field - Field element
   * @param {Object} fieldConfig - Field configuration
   * @param {boolean} showError - Whether to show error immediately
   * @returns {Object}
   */
  validateField(field, fieldConfig = {}, showError = true) {
    if (!field) {
      return { isValid: true, errorMessage: '' };
    }

    fieldConfig = fieldConfig || {};

    const fieldId = field.name || field.id;
    const fieldType = field.type || fieldConfig.type || '';
    const isRequired = field.hasAttribute('required') || fieldConfig.required === 'y';

    // Flatpickr altInput is a visible clone without the stored Y-m-d value.
    if (field.previousElementSibling && field.previousElementSibling._flatpickr) {
      return { isValid: true, errorMessage: '' };
    }

    const style = window.getComputedStyle(field);
    const isVisible = style.display !== 'none' &&
                      style.visibility !== 'hidden' &&
                      !field.closest('.d-none');

    // Skip validation for hidden fields
    if (!isVisible) {
      return { isValid: true, errorMessage: '' };
    }

    let validationResult = { isValid: true, errorMessage: '' };

    // Required validation
    if (isRequired) {
      validationResult = FieldValidators.validateRequired(field, this.translator);
      if (!validationResult.isValid) {
        if (showError) {
          ErrorManager.showError(field, validationResult.errorMessage);
        }
        this.formState.setValidationState(fieldId, false, validationResult.errorMessage);
        return validationResult;
      }
    }

    // Skip further validation if field is empty and not required
    const fileHasSelection = field.type === 'file' && field.files && field.files.length > 0;
    if (!fileHasSelection && (!field.value || !field.value.trim())) {
      if (showError) {
        ErrorManager.clearError(field);
      }
      this.formState.setValidationState(fieldId, true, '');
      return { isValid: true, errorMessage: '' };
    }

    // Check for Israeli ID/phone validation first (before type-specific validation)
    const inputFunction = field.getAttribute('data-input-function');
    if (inputFunction === 'israeliIDNumber') {
      validationResult = FieldValidators.validateIsraeliID(field, this.translator);
      if (!validationResult.isValid) {
        if (showError) {
          ErrorManager.showError(field, validationResult.errorMessage);
        }
        this.formState.setValidationState(fieldId, false, validationResult.errorMessage);
        return validationResult;
      }
    } else if (inputFunction === 'israeliPhoneNumber') {
      validationResult = FieldValidators.validateIsraeliPhone(field, this.translator);
      if (!validationResult.isValid) {
        if (showError) {
          ErrorManager.showError(field, validationResult.errorMessage);
        }
        this.formState.setValidationState(fieldId, false, validationResult.errorMessage);
        return validationResult;
      }
    }

    // Type-specific validation
    switch (fieldType) {
      case 'email':
        validationResult = FieldValidators.validateEmail(field, this.translator);
        break;
      case 'tel':
      case 'phone':
        const countryCode = field.parentNode?.querySelector('.country-code-selector')?.value || '';
        validationResult = FieldValidators.validatePhone(field, countryCode, this.translator);
        break;
      case 'date':
        validationResult = FieldValidators.validateDate(field, this.translator, fieldConfig);
        break;
      case 'number':
        // Only validate number if not Israeli ID (which is also a number type)
        if (inputFunction !== 'israeliIDNumber') {
          validationResult = FieldValidators.validateNumber(field, this.translator);
        }
        break;
      default:
        // Check for other custom input functions
        if (inputFunction && inputFunction !== 'israeliIDNumber' && inputFunction !== 'israeliPhoneNumber') {
          validationResult = FieldValidators.validateInputFunction(field, inputFunction, this.translator);
        }
    }

    // Validate maxchars if specified
    if (fieldConfig && fieldConfig.maxchars) {
      const maxChars = parseInt(fieldConfig.maxchars);
      if (!isNaN(maxChars) && maxChars > 0) {
        const maxCharsResult = FieldValidators.validateMaxChars(field, maxChars, this.translator);
        if (!maxCharsResult.isValid) {
          validationResult = maxCharsResult;
        }
      }
    }

    // Update form state
    this.formState.setValidationState(fieldId, validationResult.isValid, validationResult.errorMessage);

    // Show or clear error and update visual state
    if (showError) {
      if (validationResult.isValid) {
        ErrorManager.clearError(field);
        // Only show green if field has value
        if (field.value && field.value.trim() !== '') {
          field.classList.remove('is-invalid');
          field.classList.add('is-valid');
        } else {
          // Empty valid field - remove both classes
          field.classList.remove('is-invalid', 'is-valid');
        }
      } else {
        ErrorManager.showError(field, validationResult.errorMessage);
        field.classList.remove('is-valid');
        field.classList.add('is-invalid');
      }
    }

    return validationResult;
  }

  /**
   * Check if field is visible
   * @param {Element} field - Field element
   * @returns {boolean}
   */
  isFieldVisible(field) {
    const style = window.getComputedStyle(field);
    return style.display !== 'none' && 
           style.visibility !== 'hidden' && 
           !field.closest('.d-none');
  }

  /**
   * Validate all fields on current page
   * @param {number} pageNum - Page number
   * @returns {boolean}
   */
  validatePage(pageNum) {
    const page = document.querySelector(`.page[data-num="${pageNum}"]`);
    if (!page) return true;

    const fields = page.querySelectorAll('input:not([type="hidden"]), select, textarea');
    let isValid = true;

    fields.forEach(field => {
      if (this.isFieldVisible(field)) {
        const fieldConfig = this.getFieldConfig(field);
        const result = this.validateField(field, fieldConfig, true);
        if (!result.isValid) {
          isValid = false;
        }
      }
    });

    return isValid;
  }

  /**
   * Validate all fields in form
   * @returns {boolean}
   */
  validateAll() {
    const fields = document.querySelectorAll('input:not([type="hidden"]), select, textarea');
    let isValid = true;

    fields.forEach(field => {
      if (this.isFieldVisible(field)) {
        const fieldConfig = this.getFieldConfig(field);
        const result = this.validateField(field, fieldConfig, true);
        if (!result.isValid) {
          isValid = false;
        }
      }
    });

    return isValid;
  }

  /**
   * Validate required fields only
   * @returns {Array} Array of invalid required fields
   */
    validateRequiredFields() {
    const requiredFields = document.querySelectorAll('[required]');
    const invalidFields = [];

    requiredFields.forEach(field => {
      if (this.isFieldVisible(field)) {
        const result = FieldValidators.validateRequired(field, this.translator);
        if (!result.isValid) {
          invalidFields.push({
            field,
            errorMessage: result.errorMessage
          });
          ErrorManager.showError(field, result.errorMessage);
        } else {
          ErrorManager.clearError(field);
        }
      }
    });

    return invalidFields;
  }

  /**
   * Check if form is valid
   * @returns {boolean}
   */
  isFormValid() {
    return this.validateAll();
  }

  /**
   * Clear all validation errors
   */
  clearAll() {
    ErrorManager.clearAllErrors();
    this.formState.clearAllValidationStates();
  }

  /**
   * Get field configuration for a field element
   * @param {Element} field - Field element
   * @returns {Object} Field configuration
   */
  getFieldConfig(field) {
    if (!this.formConfig || !field) {
      return {};
    }

    const fieldId = field.name || field.id;
    if (!fieldId) {
      return {};
    }

    // Checkbox groups use name like "field_123[]"; config uses id "field_123"
    const rawId = fieldId.endsWith('[]') ? fieldId.slice(0, -2) : fieldId;
    return FormConfig.getFieldById(this.formConfig, rawId) || {};
  }
}

